import { useEffect } from "react";
import { useAuthStore } from "../store/auth";
import { useConfig } from "../contexts/ConfigContext";
import { useLayerActions } from "../store/layer";

/**
 * useLayerInitialization
 *
 * Bootstraps and maintains the layer state lifecycle in the Layer Zustand store.
 *
 * Responsibilities:
 * - Initializes map layers when:
 *   - A user logs in
 *   - Map layer configuration changes
 * - Resets all layer-related session state when the user logs out
 *
 * Why this hook exists:
 * - Zustand stores are global and do not have React lifecycle awareness.
 * - This hook bridges React lifecycle events (auth + config changes)
 *   with imperative store actions.
 *
 * Usage:
 * - Call **once** at the application or layout level (e.g. App, MapLayout).
 * - Do NOT use inside multiple components to avoid duplicate initialization.
 *
 * Dependencies:
 * - Auth store → determines login/logout lifecycle
 * - Config context → provides map layer configuration
 *
 * Notes:
 * - This hook intentionally does NOT use or depend on React Context for layers.
 * - All layer state lives exclusively in the Layer Zustand store.
 */
export const useLayerInitialization = () => {
  const { user: profile } = useAuthStore();
  const config = useConfig();

  const { initializeLayers, reset } = useLayerActions();

  // Initialize / reinitialize layers when user or config changes
  useEffect(() => {
    initializeLayers(config.mapLayers);
  }, [profile, config.mapLayers, initializeLayers]);

  // Clear all layer session state on logout
  useEffect(() => {
    if (!profile) {
      reset(config.mapLayers);
    }
  }, [profile, reset, config.mapLayers]);
};
